import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Bell, Check, CheckCheck, Package, AlertTriangle, ShieldOff, X, MessageSquare, Ticket, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { formatInCST } from '../utils/timezone';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.unread_count);
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications?limit=20');
      const notifs = res.data.notifications || [];
      
      // Also fetch security alerts for admins
      try {
        const alertsRes = await api.get('/auth/security-alerts');
        const alerts = alertsRes.data || [];
        alerts.forEach(a => {
          notifs.unshift({
            id: a.id,
            type: 'security_alert',
            title: 'Security Alert',
            message: a.message,
            read: a.acknowledged,
            created_at: a.created_at,
            _is_security_alert: true,
          });
        });
      } catch {}
      
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    } catch {}
  };

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const getIcon = (type) => {
    if (type === 'security_alert') return <ShieldOff className="h-4 w-4 text-red-600" />;
    if (type === 'sync_auto_excluded') return <ShieldOff className="h-4 w-4 text-red-500" />;
    if (type === 'sync_failure_threshold') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    if (type === 'comment_added' || type === 'customer_reply' || type === 'internal_note') return <MessageSquare className="h-4 w-4 text-blue-500" />;
    if (type === 'new_ticket') return <Ticket className="h-4 w-4 text-green-500" />;
    return <Bell className="h-4 w-4 text-gray-500" />;
  };

  const handleNotificationClick = (n) => {
    if (!n.read) {
      if (n._is_security_alert) {
        api.post(`/auth/security-alerts/${n.id}/acknowledge`).catch(() => {});
      } else {
        markRead(n.id);
      }
    }
    setOpen(false);
    if (n.ticket_id) {
      navigate(`/tickets/${n.ticket_id}`);
    } else if (n.invoice_id) {
      navigate(`/invoices/${n.invoice_id}`);
    } else if (n.type === 'sync_auto_excluded' || n.type === 'sync_failure_threshold') {
      navigate('/ninjarmm');
    } else if (n.type === 'security_alert') {
      // Just close — alert is acknowledged
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-xl shadow-xl border z-50 max-h-[480px] flex flex-col" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1" data-testid="mark-all-read">
                  <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors ${!n.read ? 'bg-blue-50/50' : ''}`}
                  onClick={() => handleNotificationClick(n)}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">{getIcon(n.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{n.title}</span>
                        {!n.read && <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {n.created_at ? formatInCST(n.created_at, 'MMM d, h:mm a') : ''}
                        {n.customer_name ? ` | ${n.customer_name}` : ''}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
